import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faNetworkWired,
  faCheckCircle,
  faExclamationTriangle,
  faPlus,
  faArrowUp,
  faArrowDown,
  faClock,
  faTasks,
  faCog,
  faArrowRight,
  faShieldAlt,
  faChartLine,
  faComments,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';

import MainLayout from '../Layout/MainLayout';
import { dashboardAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { taskService } from '../../services/taskService';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalConnectors: 0,
    activeConnectors: 0,
    totalExecutions: 0,
    successfulExecutions: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemStatus, setSystemStatus] = useState({
    api: 'online',
    database: 'online',
    agents: 'online'
  });
  const [loading, setLoading] = useState(true);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [activeTaskTab, setActiveTaskTab] = useState('pending');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setTasksLoading(true);

      // Load pending tasks (includes todo, pending, in_progress)
      const pendingData = await taskService.getMyTasks('pending');
      const inProgressData = await taskService.getMyTasks('in_progress');
      const todoData = await taskService.getMyTasks('todo');

      // Combine pending and in_progress tasks
      const allPending = [
        ...(pendingData.tasks || []),
        ...(inProgressData.tasks || []),
        ...(todoData.tasks || [])
      ];
      setPendingTasks(allPending);

      // Load completed tasks
      const completedData = await taskService.getMyTasks('completed');
      setCompletedTasks(completedData.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setPendingTasks([]);
      setCompletedTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleGoToChat = (task) => {
    try {
      const taskData = typeof task.task_data === 'string'
        ? JSON.parse(task.task_data)
        : task.task_data;

      if (taskData && taskData.session_id) {
        const { session_id, workflow_id, workflow_name } = taskData;
        if (workflow_id) {
          navigate(`/chat/${session_id}?workflow=${workflow_id}&workflowName=${encodeURIComponent(workflow_name || 'Workflow')}&mode=workflow&version=v2`);
        } else {
          navigate(`/chat/${session_id}`);
        }
      }
    } catch (error) {
      console.error('Error navigating to chat:', error);
    }
  };

  const handleGoToNetSuiteBill = (task) => {
    try {
      const completionData = typeof task.completion_data === 'string'
        ? JSON.parse(task.completion_data)
        : task.completion_data;

      if (completionData && completionData.netsuite_bill_url) {
        window.open(completionData.netsuite_bill_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error opening NetSuite bill:', error);
    }
  };

  const formatTaskDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Agents removed - now displayed in agents dashboard
      
      // Load dashboard stats
      try {
        const statsResponse = await dashboardAPI.getStats();
        console.log('📊 [DEBUG] Dashboard stats response:', statsResponse);
        if (statsResponse && statsResponse.success && statsResponse.data) {
          setStats(statsResponse.data);
        } else {
          // If API doesn't return expected format, use default values
          console.log('📊 [DEBUG] Using default stats values');
          setStats({
            totalAgents: 2,
            activeAgents: 2,
            totalConnectors: 1,
            activeConnectors: 1,
            totalExecutions: 15,
            successfulExecutions: 12
          });
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
        // Set default values
        setStats({
          totalAgents: 2,
          activeAgents: 2,
          totalConnectors: 1,
          activeConnectors: 1,
          totalExecutions: 15,
          successfulExecutions: 12
        });
      }

      // Load recent activity
      try {
        const activityResponse = await dashboardAPI.getRecentActivity();
        console.log('📈 [DEBUG] Dashboard activity response:', activityResponse);
        if (activityResponse && activityResponse.success && activityResponse.data) {
          setRecentActivity(activityResponse.data);
        } else {
          console.log('📈 [DEBUG] Using default activity values');
          setRecentActivity([
          {
            id: 1,
            type: 'agent_execution',
            message: 'NetSuite Integration agent executed successfully',
            timestamp: new Date().toISOString(),
            status: 'success'
          },
          {
            id: 2,
            type: 'connector_test',
            message: 'API connector connection test completed',
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            status: 'success'
          },
          {
            id: 3,
            type: 'agent_discovery',
            message: 'New agent discovered: Data Processing',
            timestamp: new Date(Date.now() - 7200000).toISOString(),
            status: 'info'
          }
        ]);
        }
      } catch (error) {
        console.error('Failed to load activity:', error);
        setRecentActivity([
          {
            id: 1,
            type: 'agent_execution',
            message: 'NetSuite Integration agent executed successfully',
            timestamp: new Date().toISOString(),
            status: 'success'
          }
        ]);
      }

      // Load system status
      try {
        const statusResponse = await dashboardAPI.getSystemStatus();
        if (statusResponse.success) {
          setSystemStatus(statusResponse.data);
        }
      } catch (error) {
        console.error('Failed to load system status:', error);
      }

    } catch (error) {
      console.error('Dashboard loading error:', error);
    } finally {
      setLoading(false);
    }
  };


  const StatCard = ({ title, value, subtitle, icon, color, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="card p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${color} flex items-center justify-center text-white shadow-lg`}>
          <FontAwesomeIcon icon={icon} className="text-xl" />
        </div>
        {trend && (
          <div className={`flex items-center space-x-1 text-sm ${trend.direction === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            <FontAwesomeIcon icon={trend.direction === 'up' ? faArrowUp : faArrowDown} className="text-xs" />
            <span>{trend.value}</span>
          </div>
        )}
      </div>
      <div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          {value}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
          {title}
        </p>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {subtitle}
          </p>
        )}
      </div>
    </motion.div>
  );

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'agent_execution':
        return faRobot;
      case 'connector_test':
        return faNetworkWired;
      case 'agent_discovery':
        return faPlus;
      default:
        return faClock;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  if (loading) {
    return (
      <MainLayout title="Dashboard">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const isAdmin = user?.role === 'admin';

  // Get current time for greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Customer Dashboard - Professional view
  if (!isAdmin) {
    return (
      <MainLayout title="Dashboard">
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
          {/* Hero Section */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500"></div>
            <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
            <div className="relative px-4 sm:px-6 py-6 sm:py-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="max-w-4xl mx-auto"
              >
                <p className="text-blue-200 text-xs sm:text-sm font-medium tracking-wide uppercase mb-1">{getGreeting()}</p>
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-2">
                  Welcome, {user?.name || user?.username || 'User'}
                </h1>
                <p className="text-blue-100 text-sm sm:text-base max-w-xl">
                  Access your AI-powered tools and manage your workflows efficiently
                </p>
              </motion.div>
            </div>
          </div>

          {/* Main Content */}
          <div className="px-6 py-8 mt-4">
            <div className="max-w-6xl mx-auto">
              {/* Quick Access Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-8"
              >
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faChartLine} className="text-indigo-500" />
                  Quick Access
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* AI Agents Card */}
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/agents')}
                    className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="relative">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/25">
                        <FontAwesomeIcon icon={faRobot} className="text-xl text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                        AI Agents
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Access intelligent automation tools
                      </p>
                      <div className="flex items-center text-indigo-600 dark:text-indigo-400 text-sm font-medium group-hover:gap-2 transition-all">
                        <span>Open</span>
                        <FontAwesomeIcon icon={faArrowRight} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.div>

                  {/* Task Management Card */}
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/tasks')}
                    className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="relative">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/25">
                        <FontAwesomeIcon icon={faTasks} className="text-xl text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                        Tasks
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Manage and track your tasks
                      </p>
                      <div className="flex items-center text-emerald-600 dark:text-emerald-400 text-sm font-medium group-hover:gap-2 transition-all">
                        <span>Open</span>
                        <FontAwesomeIcon icon={faArrowRight} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.div>

                  {/* Connectors Card */}
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/connectors')}
                    className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="relative">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/25">
                        <FontAwesomeIcon icon={faNetworkWired} className="text-xl text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                        Connectors
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Manage service integrations
                      </p>
                      <div className="flex items-center text-violet-600 dark:text-violet-400 text-sm font-medium group-hover:gap-2 transition-all">
                        <span>Open</span>
                        <FontAwesomeIcon icon={faArrowRight} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.div>

                  {/* Settings Card */}
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/settings')}
                    className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-slate-500/10 to-gray-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="relative">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-600 to-gray-700 flex items-center justify-center mb-4 shadow-lg shadow-slate-500/25">
                        <FontAwesomeIcon icon={faCog} className="text-xl text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                        Settings
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Configure your preferences
                      </p>
                      <div className="flex items-center text-slate-600 dark:text-slate-400 text-sm font-medium group-hover:gap-2 transition-all">
                        <span>Open</span>
                        <FontAwesomeIcon icon={faArrowRight} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Tasks Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-8"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {/* Task Header with Tabs */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <FontAwesomeIcon icon={faTasks} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">My Tasks</h3>
                    </div>
                    <button
                      onClick={() => navigate('/tasks')}
                      className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors flex items-center gap-1"
                    >
                      View All
                      <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                    </button>
                  </div>

                  {/* Tab Buttons */}
                  <div className="flex border-b border-gray-100 dark:border-gray-700">
                    <button
                      onClick={() => setActiveTaskTab('pending')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                        activeTaskTab === 'pending'
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <FontAwesomeIcon icon={faClock} className="text-xs" />
                        Pending
                        {pendingTasks.length > 0 && (
                          <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs px-2 py-0.5 rounded-full">
                            {pendingTasks.length}
                          </span>
                        )}
                      </span>
                      {activeTaskTab === 'pending' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500"></div>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTaskTab('completed')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                        activeTaskTab === 'completed'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                        Completed
                        {completedTasks.length > 0 && (
                          <span className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs px-2 py-0.5 rounded-full">
                            {completedTasks.length}
                          </span>
                        )}
                      </span>
                      {activeTaskTab === 'completed' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500"></div>
                      )}
                    </button>
                  </div>

                  {/* Task List */}
                  <div className="p-4 max-h-96 overflow-y-auto">
                    {tasksLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent"></div>
                      </div>
                    ) : (
                      <>
                        {activeTaskTab === 'pending' && (
                          pendingTasks.length === 0 ? (
                            <div className="text-center py-8">
                              <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-gray-300 dark:text-gray-600 mb-3" />
                              <p className="text-sm text-gray-500 dark:text-gray-400">No pending tasks</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {pendingTasks.slice(0, 5).map((task) => (
                                <div
                                  key={task.id}
                                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {task.title}
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {formatTaskDate(task.created_at)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 ml-3">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      task.status === 'in_progress'
                                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                        : 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                                    }`}>
                                      {task.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                    </span>
                                    {task.task_type === 'approval' && task.task_data && (
                                      <button
                                        onClick={() => handleGoToChat(task)}
                                        className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs font-medium rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all shadow-sm"
                                      >
                                        <FontAwesomeIcon icon={faComments} className="mr-1" />
                                        Go to Chat
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}

                        {activeTaskTab === 'completed' && (
                          completedTasks.length === 0 ? (
                            <div className="text-center py-8">
                              <FontAwesomeIcon icon={faTasks} className="text-3xl text-gray-300 dark:text-gray-600 mb-3" />
                              <p className="text-sm text-gray-500 dark:text-gray-400">No completed tasks yet</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {completedTasks.slice(0, 5).map((task) => {
                                const hasNetSuiteUrl = (() => {
                                  try {
                                    const completionData = typeof task.completion_data === 'string'
                                      ? JSON.parse(task.completion_data)
                                      : task.completion_data;
                                    return completionData && completionData.netsuite_bill_url;
                                  } catch {
                                    return false;
                                  }
                                })();

                                return (
                                  <div
                                    key={task.id}
                                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {task.title}
                                      </h4>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Completed {formatTaskDate(task.updated_at)}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2 ml-3">
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                        Done
                                      </span>
                                      {hasNetSuiteUrl && (
                                        <button
                                          onClick={() => handleGoToNetSuiteBill(task)}
                                          className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-teal-600 text-white text-xs font-medium rounded-lg hover:from-green-600 hover:to-teal-700 transition-all shadow-sm"
                                        >
                                          <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                                          View Bill
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Info Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Getting Started */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faRobot} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Getting Started</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                    Start by exploring the AI Agents section to discover automation tools designed to streamline your workflow and boost productivity.
                  </p>
                  <button
                    onClick={() => navigate('/agents')}
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Explore Agents
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                  </button>
                </div>

                {/* Security Info */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Secure Platform</h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                    Your data is protected with enterprise-grade security. All connections are encrypted and your information is handled with care.
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      All Systems Operational
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Admin Dashboard - Professional view
  return (
    <MainLayout title="Dashboard">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
        {/* Hero Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900"></div>
          <div className="absolute inset-0 opacity-30" style={{backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)'}}></div>
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
          <div className="relative px-6 py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-7xl mx-auto"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-indigo-300 text-sm font-medium tracking-wide uppercase mb-1">
                    Admin Dashboard
                  </p>
                  <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {getGreeting()}, {user?.name || 'Admin'}
                  </h1>
                  <p className="text-slate-300 text-sm md:text-base">
                    Monitor your platform's performance and manage your AI agents
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm font-medium">All Systems Operational</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="px-6 py-8 -mt-4">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Stats Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
            >
              {/* Agents Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                    </div>
                    <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 rounded-lg">
                      <FontAwesomeIcon icon={faArrowUp} className="text-xs" />
                      Active
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stats?.totalAgents || 0}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Total AI Agents</p>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">{stats?.activeAgents || 0} currently active</span>
                  </div>
                </div>
              </div>

              {/* Connectors Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                      <FontAwesomeIcon icon={faNetworkWired} className="text-white text-lg" />
                    </div>
                    <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 rounded-lg">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                      Connected
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stats?.totalConnectors || 0}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Integrations</p>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">{stats?.activeConnectors || 0} active connections</span>
                  </div>
                </div>
              </div>

              {/* Executions Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                      <FontAwesomeIcon icon={faChartLine} className="text-white text-lg" />
                    </div>
                    <span className="flex items-center gap-1 text-violet-600 text-sm font-medium bg-violet-50 dark:bg-violet-900/30 px-2 py-1 rounded-lg">
                      <FontAwesomeIcon icon={faArrowUp} className="text-xs" />
                      +12%
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{stats?.totalExecutions || 0}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Executions Today</p>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-violet-600 dark:text-violet-400 text-sm font-medium">{stats?.successfulExecutions || 0} successful</span>
                  </div>
                </div>
              </div>

              {/* Tasks Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur-xl opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                      <FontAwesomeIcon icon={faTasks} className="text-white text-lg" />
                    </div>
                    <span className="flex items-center gap-1 text-amber-600 text-sm font-medium bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded-lg">
                      <FontAwesomeIcon icon={faClock} className="text-xs" />
                      Pending
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{pendingTasks.length}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Pending Tasks</p>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-amber-600 dark:text-amber-400 text-sm font-medium">{completedTasks.length} completed today</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Activity - Takes 2 columns */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faClock} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
                  </div>
                  <button className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition-colors">
                    View All
                  </button>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {recentActivity && recentActivity.length > 0 ? (
                      recentActivity.map((activity, index) => (
                        <motion.div
                          key={activity.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            activity.status === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                            activity.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                            activity.status === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                            'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          }`}>
                            <FontAwesomeIcon icon={getActivityIcon(activity.type)} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.message}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatTimeAgo(activity.timestamp)}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-lg text-xs font-medium shrink-0 ${
                            activity.status === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                            activity.status === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                            activity.status === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                            'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          }`}>
                            {activity.status}
                          </span>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <FontAwesomeIcon icon={faClock} className="text-4xl text-gray-300 dark:text-gray-600 mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">No recent activity</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* System Status - Takes 1 column */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Status</h3>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {systemStatus && Object.entries(systemStatus).map(([service, status], index) => (
                    <motion.div
                      key={service}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${status === 'online' ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></div>
                        <span className="font-medium capitalize text-gray-900 dark:text-white text-sm">{service}</span>
                      </div>
                      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wide ${
                        status === 'online'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      }`}>
                        {status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500" />
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/agents')}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 text-left overflow-hidden transition-all hover:shadow-xl"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/25">
                      <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Manage Agents</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">View and configure AI agents</p>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/connectors')}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 text-left overflow-hidden transition-all hover:shadow-xl"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/25">
                      <FontAwesomeIcon icon={faNetworkWired} className="text-white text-lg" />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Connectors</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage integrations</p>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/users')}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 text-left overflow-hidden transition-all hover:shadow-xl"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/25">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-white text-lg" />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">User Management</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage users & permissions</p>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/workflows')}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 text-left overflow-hidden transition-all hover:shadow-xl"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-full -translate-y-12 translate-x-12 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/25">
                      <FontAwesomeIcon icon={faChartLine} className="text-white text-lg" />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Workflows</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Monitor executions</p>
                  </div>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;